import { AxiosRequest } from "_services/api";
import { toastErro } from "components/Toast";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FILTROS_LOTE_VAZIOS,
  ORDENACAO_LOTE_PADRAO,
} from "../constants/filtrosLote";
import { TipoSolicitacaoPlaca } from "../constants/tipoSolicitacaoPlaca";
import { IFiltrosLote } from "../interfaces/IFiltrosLote";
import { ILoteSolicitacao } from "../interfaces/ILoteSolicitacao";
import { IEstadoListagemLotes } from "../interfaces/INavegacaoSolicitacaoPlaca";
import { DirecaoOrdenacao, IOrdenacao } from "../interfaces/IOrdenacao";
import { exportarLotesXlsx } from "../utils/exportarLotesXlsx";
import { mensagemDeErro } from "../utils/mensagemDeErro";
import { montarParametrosLotes } from "../utils/montarParametrosLotes";

const LIMITE_PADRAO = 50;

export function useLotes(
  tipo: TipoSolicitacaoPlaca,
  estadoInicial?: IEstadoListagemLotes
) {
  const msAw = useMemo(
    () => AxiosRequest.makeApi(process.env.REACT_APP_MS_AW),
    []
  );
  const [filtros, setFiltros] = useState<IFiltrosLote>(
    estadoInicial?.filtros ?? FILTROS_LOTE_VAZIOS
  );
  const [pagina, setPagina] = useState(estadoInicial?.pagina ?? 1);
  const [paginaInicial, setPaginaInicial] = useState(
    estadoInicial?.pagina ?? 1
  );
  const [limite, setLimite] = useState(estadoInicial?.limite ?? LIMITE_PADRAO);
  const [ordenacao, setOrdenacao] = useState<IOrdenacao>(
    estadoInicial?.ordenacao ?? ORDENACAO_LOTE_PADRAO
  );
  const [versao, setVersao] = useState(0);
  const [reiniciarPaginacao, setReiniciarPaginacao] = useState(false);
  const [lotes, setLotes] = useState<ILoteSolicitacao[]>([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const ultimaBusca = useRef(0);
  const limiteAtual = useRef(limite);
  const ordenacaoAtual = useRef(ordenacao);

  useEffect(() => {
    const busca = ++ultimaBusca.current;

    async function buscar() {
      setCarregando(true);
      try {
        const parametros = montarParametrosLotes({
          tipo,
          filtros,
          ordenacao,
          pagina,
          limite,
        });
        const { data } = await msAw.get(
          `/solicitacao-placa/lote?${parametros}`
        );
        if (busca !== ultimaBusca.current) return;
        setLotes(data?.itens ?? []);
        setTotalRegistros(data?.meta?.total ?? 0);
      } catch (erro) {
        if (busca !== ultimaBusca.current) return;
        setLotes([]);
        setTotalRegistros(0);
        toastErro(mensagemDeErro(erro, "Erro ao buscar os lotes"));
      } finally {
        if (busca === ultimaBusca.current) setCarregando(false);
      }
    }

    buscar();
  }, [msAw, tipo, filtros, pagina, limite, ordenacao, versao]);

  // O reset da paginação do react-data-table volta para o paginationDefaultPage, não para a página 1.
  const voltarParaPrimeiraPagina = useCallback(() => {
    setPagina(1);
    setPaginaInicial(1);
    setReiniciarPaginacao((valor) => !valor);
  }, []);

  const recarregar = useCallback(() => {
    setVersao((valor) => valor + 1);
  }, []);

  const aplicarFiltros = useCallback(
    (novosFiltros: IFiltrosLote) => {
      setFiltros(novosFiltros);
      recarregar();
      voltarParaPrimeiraPagina();
    },
    [recarregar, voltarParaPrimeiraPagina]
  );

  const mudarLimite = useCallback(
    (novoLimite: number) => {
      // No StrictMode o react-data-table redispara o limite atual no mount.
      if (novoLimite === limiteAtual.current) return;
      limiteAtual.current = novoLimite;
      setLimite(novoLimite);
      voltarParaPrimeiraPagina();
    },
    [voltarParaPrimeiraPagina]
  );

  // O react-data-table já volta sozinho para a página 1 quando a ordenação muda.
  const ordenar = useCallback((campo: string, direcao: DirecaoOrdenacao) => {
    // No StrictMode o react-data-table redispara a ordenação atual no mount.
    const atual = ordenacaoAtual.current;
    if (campo === atual.campo && direcao === atual.direcao) return;
    ordenacaoAtual.current = { campo, direcao };
    setOrdenacao({ campo, direcao });
    setPagina(1);
  }, []);

  const exportar = useCallback(async () => {
    setExportando(true);
    try {
      const parametros = montarParametrosLotes({ tipo, filtros, ordenacao });
      const { data } = await msAw.get(
        `/solicitacao-placa/lote/itens?${parametros}`
      );
      exportarLotesXlsx(data?.itens ?? []);
    } catch (erro) {
      toastErro(mensagemDeErro(erro, "Erro ao exportar os lotes"));
    } finally {
      setExportando(false);
    }
  }, [msAw, tipo, filtros, ordenacao]);

  const estado = useMemo<IEstadoListagemLotes>(
    () => ({ filtros, pagina, limite, ordenacao }),
    [filtros, pagina, limite, ordenacao]
  );

  return {
    lotes,
    totalRegistros,
    carregando,
    limite,
    reiniciarPaginacao,
    paginaInicial,
    aplicarFiltros,
    setPagina,
    mudarLimite,
    ordenar,
    recarregar,
    exportar,
    exportando,
    estado,
  };
}
